// Pure helpers for the "New Customers Secured" dashboard widget. Kept free of
// React/redux so date handling and the API-payload mapping can be unit tested in
// isolation (mirrors customerPerformanceHelpers).
//
// The widget now consumes pre-computed figures from the NewCustomerCount
// dashboard endpoint, so `mapNewCustomerCounts` is a shape mapping only (no
// counting). `getPeriodRanges` / `toDay` are retained because revenueProfitHelpers
// re-exports them.

const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
const toYmd = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Normalize a date value (ISO datetime, e.g. "2026-06-15T09:00:00Z", or a plain
// "2026-06-15") to a YYYY-MM-DD string. Returns "" when absent/unparseable.
export const toDay = (value) => {
  if (!value) return "";
  const s = String(value);
  const isoDay = s.match(/^\d{4}-\d{2}-\d{2}/);
  if (isoDay) return isoDay[0];
  const d = new Date(s);
  return isNaN(d.getTime()) ? "" : toYmd(d);
};

// The three reporting windows, all "to-date" and anchored to `now`:
//   week  = start of the current calendar week (Sunday) → today
//   month = first of the current month → today  (Month-To-Date)
//   year  = Jan 1 of the current year → today    (Year-To-Date)
export const getPeriodRanges = (now = new Date()) => {
  const year = now.getFullYear();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay()); // back up to Sunday
  const toDate = toYmd(now);
  return {
    week: { fromDate: toYmd(weekStart), toDate },
    month: { fromDate: `${year}-${pad(now.getMonth() + 1)}-01`, toDate },
    year: { fromDate: `${year}-01-01`, toDate },
  };
};

const EMPTY_RANGES = {
  week: { fromDate: "", toDate: "" },
  month: { fromDate: "", toDate: "" },
  year: { fromDate: "", toDate: "" },
};

// Map the pre-computed NewCustomerCount API payload (axios `res.data`) to the
// widget's view model. The backend scopes the response to the caller (self vs
// all), so this is a pure shape mapping. Figures live in `result[0]`.
export const mapNewCustomerCounts = (apiData) => {
  const row =
    apiData && Array.isArray(apiData.result) ? apiData.result[0] : null;
  if (!row) {
    return { totals: { week: 0, mtd: 0, ytd: 0 }, reps: [], ranges: EMPTY_RANGES };
  }

  const totals = {
    week: row.weekCount || 0,
    mtd: row.monthToDateCount || 0,
    ytd: row.yearToDateCount || 0,
  };

  const reps = (Array.isArray(row.salesRepBreakdown) ? row.salesRepBreakdown : [])
    .map((r) => ({
      repName: r.salesPersonName,
      wtd: r.weekCount || 0,
      mtd: r.monthToDateCount || 0,
      ytd: r.yearToDateCount || 0,
      salesPersonId: r.salesPersonId,
    }))
    .sort(
      (a, b) => b.ytd - a.ytd || (a.repName || "").localeCompare(b.repName || "")
    );

  const toDate = toDay(row.toDate);
  const ranges = {
    week: { fromDate: toDay(row.weekFromDate), toDate },
    month: { fromDate: toDay(row.mtdFromDate), toDate },
    year: { fromDate: toDay(row.ytdFromDate), toDate },
  };

  return { totals, reps, ranges };
};
